import React from 'react';

import { useParamState } from '@lib';
import { DateRange, isDateRange } from 'react-day-picker';

export interface DateSelection {
  from: Date | undefined;
  to: Date | undefined;
  isSameDay: boolean;
}

export const useDateRange = (type: 'param' | 'react', initial: DateRange | undefined) => {
  const [dataState, setDataState] = React.useState<DateRange | undefined>(initial);
  const [dateParam, setDateParam] = useParamState<DateRange | undefined>('dateRange', initial);

  const date = type === 'param' ? dateParam : dataState;
  const setDateRange = type === 'param' ? setDateParam : setDataState;

  const { from, to } = date || {};

  const setDate = (day: Date) => {
    let newDateRange = { from, to };

    if (from && to) {
      // If both dates are selected, start a new range with the new day as 'from'
      newDateRange = { from: day, to: undefined };
    } else if (from && !to) {
      // If only 'from' is selected, set 'to', ensuring order is maintained
      if (new Date(day) < new Date(from)) {
        // New day is earlier than 'from', swap them
        newDateRange = { from: day, to: from };
      } else {
        // New day is later than 'from', set as 'to'
        newDateRange = { from, to: day };
      }
    } else {
      // If no dates are selected, set the new day as 'from'
      newDateRange = { from: day, to: undefined };
    }

    setDateRange(newDateRange);
    return newDateRange;
  };

  return [date, setDate, setDateRange] as const;
};

export function getSelectedDates(selected: unknown): DateSelection {
  if (selected instanceof Date) {
    return { from: selected, to: selected, isSameDay: true };
  }

  if (isDateRange(selected)) {
    const isSameDay = selected?.from === selected?.to || !selected?.from || !selected?.to;

    return {
      from: selected?.from,
      to: selected?.to,
      isSameDay,
    };
  }

  const now = new Date();

  return { from: now, to: now, isSameDay: true };
}
